import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { HQAuthProvider, useHQAuth } from "@/hq/HQAuthContext";
import HQLayout from "@/hq/HQLayout";
import HQLogin from "@/hq/HQLogin";

// New Control Centre primary sections
import HQExecutiveDashboard from "@/hq/HQExecutiveDashboard";
import HQClients from "@/hq/clients/HQClients";
import HQClientProfile from "@/hq/clients/HQClientProfile";
import HQSubscriptions from "@/hq/subscriptions/HQSubscriptions";
import HQCampaignsHub from "@/hq/campaigns/HQCampaignsHub";
import HQSettings from "@/hq/settings/HQSettings";

// Legacy screens — kept mounted so deep-links, existing emails, and tabs
// that jump into them keep working. Not on the primary sidebar.
import HQLeads from "@/hq/HQLeads";
import HQLeadDetail from "@/hq/HQLeadDetail";
import HQBusinesses from "@/hq/HQBusinesses";
import HQBusinessDetail from "@/hq/HQBusinessDetail";
import HQContacts from "@/hq/HQContacts";
import HQWebsites from "@/hq/HQWebsites";
import HQActivity from "@/hq/HQActivity";
import HQOptimiseScans from "@/hq/HQOptimiseScans";
import HQOptimiseMonitoring from "@/hq/HQOptimiseMonitoring";
import HQOptimiseMonitoringDetail from "@/hq/HQOptimiseMonitoringDetail";
import HQOptimiseCommercial from "@/hq/HQOptimiseCommercial";
import HQOptimiseFix from "@/hq/HQOptimiseFix";
import HQSalesDashboard from "@/hq/HQSalesDashboard";
import HQCommandCentre from "@/hq/dashboard/HQCommandCentre";
import HQSalesProspects from "@/hq/HQSalesProspects";
import HQSalesProspectDetail from "@/hq/HQSalesProspectDetail";
import HQSalesCampaigns from "@/hq/HQSalesCampaigns";
import HQSalesCampaignDetail from "@/hq/HQSalesCampaignDetail";
import HQSalesOpportunities from "@/hq/HQSalesOpportunities";
import HQInvoices, { HQInvoiceNew, HQInvoiceDetail } from "@/hq/HQInvoices";

function Protected({ children }) {
  const { admin } = useHQAuth();
  if (admin === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFFDF6]" data-testid="hq-loading">
        <div className="text-[#8A8A8A] text-sm tracking-[0.24em] uppercase">Loading…</div>
      </div>
    );
  }
  if (!admin) return <Navigate to="/hq/login" replace />;
  return children;
}

function LoggedInGate() {
  const { admin } = useHQAuth();
  if (admin === null) return null;
  if (admin) return <Navigate to="/hq/dashboard" replace />;
  return <HQLogin />;
}

export default function HQRoot() {
  return (
    <HQAuthProvider>
      <Toaster theme="light" position="top-center" richColors />
      <Routes>
        <Route path="/login" element={<LoggedInGate />} />
        <Route element={<Protected><HQLayout /></Protected>}>
          <Route index element={<Navigate to="dashboard" replace />} />

          {/* Primary 5-section Control Centre */}
          <Route path="dashboard" element={<HQCommandCentre />} />
          <Route path="dashboard/legacy" element={<HQExecutiveDashboard />} />
          <Route path="clients" element={<HQClients />} />
          <Route path="clients/:id" element={<HQClientProfile />} />
          <Route path="subscriptions" element={<HQSubscriptions />} />
          <Route path="campaigns" element={<HQCampaignsHub />} />
          <Route path="settings" element={<HQSettings />} />

          {/* Legacy deep-linked routes (kept mounted for compatibility) */}
          <Route path="leads" element={<HQLeads />} />
          <Route path="leads/:id" element={<HQLeadDetail />} />
          <Route path="businesses" element={<HQBusinesses />} />
          <Route path="businesses/:id" element={<HQBusinessDetail />} />
          <Route path="contacts" element={<HQContacts />} />
          <Route path="websites" element={<HQWebsites />} />
          <Route path="optimise" element={<Navigate to="scans" replace />} />
          <Route path="optimise/scans" element={<HQOptimiseScans />} />
          <Route path="optimise/monitoring" element={<HQOptimiseMonitoring />} />
          <Route path="optimise/monitoring/:id" element={<HQOptimiseMonitoringDetail />} />
          <Route path="optimise/commercial" element={<HQOptimiseCommercial />} />
          <Route path="optimise/subscriptions" element={<HQOptimiseCommercial />} />
          <Route path="optimise/fix" element={<HQOptimiseFix />} />
          <Route path="sales" element={<Navigate to="/hq/dashboard" replace />} />
          <Route path="sales/legacy" element={<HQSalesDashboard />} />
          <Route path="sales/prospects" element={<HQSalesProspects />} />
          <Route path="sales/prospects/:id" element={<HQSalesProspectDetail />} />
          <Route path="sales/campaigns" element={<HQSalesCampaigns />} />
          <Route path="sales/campaigns/:id" element={<HQSalesCampaignDetail />} />
          <Route path="sales/opportunities" element={<HQSalesOpportunities />} />
          <Route path="invoices" element={<HQInvoices />} />
          <Route path="invoices/new" element={<HQInvoiceNew />} />
          <Route path="invoices/:id" element={<HQInvoiceDetail />} />
          <Route path="activity" element={<HQActivity />} />
        </Route>
        <Route path="*" element={<Navigate to="/hq/dashboard" replace />} />
      </Routes>
    </HQAuthProvider>
  );
}
